import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

import { 
  editThreatActor as editThreatActorService,
  fetchThreatActorDataByID as getThreatActorService
} from 'services/modules/ecs/data';

import { 
  Card, 
  CardHeader, 
  Divider, 
  Button, 
  CardContent,
  FormControl, 
  TextField, 
  IconButton
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import { useNotification } from 'config/Notification';

const EditThreatActor = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const threat = searchParams.get("threat");

  // React router
  const navigate = useNavigate();

  // Notification
  const { openNotification } = useNotification();

  // State variables
  const [formErrors, setFormErrors] = useState({});
  const [errormessage, setErrorMessage] = useState("")
  const [formData, setFormData] = useState({
    actor_name: "",
    aliases: "",
    first_seen: "",
    last_seen: "",
    geo_origin: "",
    targeted_countries: "",
    targeted_industries: "",
    primary_motivations: "",
    tools: "",
    common_ttps: {
      tactics: "",
      techniques: "",
      recent_activity: {
        date: "",
        description: "",
        targets: [""],
        newslink: "",
      },
      links: "",
    },
    attacks: [
      {
        date: "",
        description: "",
        targets: "",
      },
    ],
    mitigation: "",
  });
  const [dynamicIOCFields, setDynamicIOCFields] = useState([]);
  const [dynamicAttacks, setDynamicAttacks] = useState([
    { date: "", description: "", targets: "" }
  ]);

  useEffect(() => {
    const fetchData = async () => {
      const params = { id: threat };
      const response = await getThreatActorService({ params });
      const threatactorData = response.data;
      const updateformData = {
        actor_name: threatactorData.actor_name? threatactorData.actor_name:"",
        aliases: threatactorData.aliases? threatactorData.aliases.join():"",
        first_seen: threatactorData.first_seen? threatactorData.first_seen:"",
        last_seen: threatactorData.last_seen? threatactorData.last_seen:"",
        geo_origin: threatactorData.geo_origin? threatactorData.geo_origin:"",
        targeted_countries: threatactorData.targeted_countries? threatactorData.targeted_countries.join():"",
        targeted_industries: threatactorData.targeted_industries? threatactorData.targeted_industries.join():"",
        primary_motivations: threatactorData.primary_motivations? threatactorData.primary_motivations.join():"",
        tools: threatactorData.tools? threatactorData.tools.join():"",
        common_ttps: {
          tactics: threatactorData.common_ttps.tactics? threatactorData.common_ttps.tactics.join():"",
          techniques: threatactorData.common_ttps.techniques? threatactorData.common_ttps.techniques.join():"",
          recent_activity: {
            date: threatactorData.common_ttps?.recent_activity?.date? threatactorData.common_ttps.recent_activity.date:"",
            description: threatactorData.common_ttps?.recent_activity?.description ? threatactorData.common_ttps.recent_activity.description :"",
            targets: threatactorData.common_ttps?.recent_activity?.targets ? threatactorData.common_ttps.recent_activity.targets.join() :"",
            newslink: threatactorData.common_ttps?.recent_activity?.newslink ? threatactorData.common_ttps.recent_activity.newslink:"",
          },
          links: threatactorData.common_ttps.links? threatactorData.common_ttps.links.join():"",
        },
        mitigation: threatactorData.mitigation? threatactorData.mitigation:"",
      }
      // Prepare dynamic IOC fields if threatactorData.ioc has keys
      const dynamicIOCFields =
        threatactorData.ioc && Object.keys(threatactorData.ioc).length > 0
          ? Object.entries(threatactorData.ioc).map(([key, value]) => ({
              fieldName: key,
              fieldValue: value,
            }))
          : [];

      setDynamicIOCFields(dynamicIOCFields);
      setDynamicAttacks(threatactorData.attacks);
      setFormData(updateformData);
    };

    if (threat) {
      fetchData();
    }
  }, [threat]);

  // Handles changes in form input fields
  const handleInputTextChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handles changes in form input fields
  const handleTTPsTextChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      common_ttps:{
        ...formData.common_ttps,
        [name]: value,
      }
    });
  };

  // Handles changes in form input fields
  const handleRecentActivityTextChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      common_ttps:{
        ...formData.common_ttps,
        recent_activity: {
          ...formData.common_ttps.recent_activity,
          [name]: value,
        },
      }
    });
  };

  // Handles adding a new dynamic field
  const handleAddField = () => {
    setDynamicIOCFields([...dynamicIOCFields, { fieldName: '', fieldValue: '' }]);
  };

  // Handles removing a dynamic field
  const handleRemoveField = (index) => {
    const updatedFields = [...dynamicIOCFields];
    updatedFields.splice(index, 1);
    setDynamicIOCFields(updatedFields);
  };

  // Handles changing a dynamic field
  const handleDynamicFieldChange = (index, fieldKey, value) => {
    const updatedFields = [...dynamicIOCFields];
    updatedFields[index][fieldKey] = value;
    setDynamicIOCFields(updatedFields);
  };

  // Add a new attack entry
  const handleAddAttack = () => {
    setDynamicAttacks([...dynamicAttacks, { date: "", description: "", targets: "" }]);
  };

  // Remove an attack entry
  const handleRemoveAttack = (index) => {
    const updatedAttacks = [...dynamicAttacks];
    updatedAttacks.splice(index, 1);
    setDynamicAttacks(updatedAttacks);
  };

  // Handle changes in attack fields
  const handleAttackFieldChange = (index, fieldKey, value) => {
    const updatedAttacks = [...dynamicAttacks];
    updatedAttacks[index][fieldKey] = value;
    setDynamicAttacks(updatedAttacks);
  };

  // This method displays the form error if the form validation is not satisfied
  const handleErrors = (fieldName) => {
    setFormErrors((prevformErrors) => ({
      ...prevformErrors,
      [fieldName]: true,
    }));
  };

  // Handles the form submission 
  const handleSubmit = async(e) => {
    try
    {
      e.preventDefault();
      const ioc = dynamicIOCFields.reduce((acc, field) => {
        acc[field.fieldName] = field?.fieldValue?.map((item) => item.trim());
        return acc;
      }, {});

      const fieldsToConvert = [
        "aliases",
        "targeted_countries",
        "targeted_industries",
        "primary_motivations",
        "tools",
      ];

      const submissionData = {
        ...formData,
        attacks: dynamicAttacks,
        ioc: ioc,
      };

      fieldsToConvert.forEach((field) => {
        if (submissionData[field]) {
          // Convert the field value into an array by splitting with ","
          submissionData[field] = submissionData[field]
            ?.split(",")
            .map((item) => item.trim());
        }
      });

      // Process techniques and techniques_targets in common_ttps
      if (submissionData.common_ttps) {
        const updatedCommonTtps = { ...submissionData.common_ttps };

        if (updatedCommonTtps.techniques) {
          updatedCommonTtps.techniques = updatedCommonTtps.techniques
            .split(",")
            .map((item) => item.trim());
        }
        if (updatedCommonTtps.tactics) {
          updatedCommonTtps.tactics = updatedCommonTtps.tactics
            ?.split(",")
            .map((item) => item.trim());
        }
        if (updatedCommonTtps.links) {
          updatedCommonTtps.links = updatedCommonTtps.links
            ?.split(",")
            .map((item) => item.trim());
        }
        submissionData.common_ttps = updatedCommonTtps;
      }

      let response=await editThreatActorService(submissionData, threat);
      
      if(response?.status === 200 || response?.status === 201)
      {
        setErrorMessage('')
        openNotification('ThreatActor updated successfully','success')
        navigate('/tip/view-threatactors')
      }
      else
      {
        setErrorMessage(response.data.message);
      }      
    }
    catch(error)
    {
    }
  };

  return (
    <div className="card-container">
      <Card className="card">
        <CardHeader
          title={
            <React.Fragment>
              <div className='card-head'>
                <div className="deflex-head">
                  <label className='label'>  Edit ThreatActor</label>
                </div>
                <div>
                  <Button variant="contained" className="back font" onClick={()=>window.history.back()}>Back</Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
        <Divider className="divider" />
        <CardContent className="center-card">
          <Card className="user-card row company-screen-bg">
            <div className="row col-lg-12">
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md ">
                <FormControl fullWidth>
                  <TextField 
                    variant="outlined" 
                    fullWidth 
                    name="actor_name" 
                    label="ThreatActor Name"
                    value={formData.actor_name} 
                    onChange={handleInputTextChange}  
                    onBlur={() => handleErrors('actor_name')}
                    error={formData.actor_name === '' && formErrors.actor_name}
                    helperText={formData.actor_name === '' && formErrors.actor_name ? 'Field is required' : ''}
                  />
                </FormControl>
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md " >
                <FormControl fullWidth>
                  <TextField  
                    variant="outlined"
                    label="Location" 
                    fullWidth 
                    name="geo_origin" 
                    value={formData.geo_origin} 
                    onChange={handleInputTextChange}
                    onBlur={() => handleErrors('geo_origin')}
                    error={formData.geo_origin === '' && formErrors.geo_origin}
                    helperText={formData.geo_origin === '' && formErrors.geo_origin ? 'Field is required' : ''}
                  /> 
                </FormControl>
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md " >
                <FormControl fullWidth>
                  <TextField 
                    variant="outlined" 
                    fullWidth 
                    name="first_seen"  
                    label="First Seen" 
                    value={formData.first_seen} 
                    onChange={handleInputTextChange}
                    onBlur={() => handleErrors('first_seen')}
                    error={formData.first_seen === '' && formErrors.first_seen}
                    helperText={formData.first_seen === '' && formErrors.first_seen ? 'Field is required' : ''}  
                  />
                </FormControl>
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md flex " >
                <TextField  
                  variant="outlined"
                  label="Last Seen" 
                  fullWidth 
                  name="last_seen" 
                  value={formData.last_seen} 
                  onChange={handleInputTextChange}
                  onBlur={() => handleErrors('last_seen')}
                  error={formData.last_seen === '' && formErrors.last_seen}
                  helperText={(formData.last_seen === '' && formErrors.last_seen) ? 'Field is required' : ''}  
                />
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md flex " >
                <TextField  
                  variant="outlined"
                  label="Aliases" 
                  fullWidth 
                  name="aliases" 
                  value={formData.aliases} 
                  onChange={handleInputTextChange}
                  onBlur={() => handleErrors('aliases')}
                  error={formData.aliases === '' && formErrors.aliases}
                  helperText={(formData.aliases === '' && formErrors.aliases) ? 'Field is required' : ''}  
                />
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md flex " >
                <TextField  
                  variant="outlined"
                  label="Tools" 
                  fullWidth 
                  name="tools" 
                  value={formData.tools} 
                  onChange={handleInputTextChange}
                  onBlur={() => handleErrors('tools')}
                  error={formData.tools === '' && formErrors.tools}
                  helperText={(formData.tools === '' && formErrors.tools) ? 'Field is required' : ''}  
                />
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md flex " >
                <TextField  
                  variant="outlined"
                  label="Targeted Countries" 
                  fullWidth 
                  name="targeted_countries" 
                  value={formData.targeted_countries} 
                  onChange={handleInputTextChange}
                  onBlur={() => handleErrors('targeted_countries')}
                  error={formData.targeted_countries === '' && formErrors.targeted_countries}
                  helperText={(formData.targeted_countries === '' && formErrors.targeted_countries) ? 'Field is required' : ''}  
                />
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md flex " >
                <TextField  
                  variant="outlined"
                  label="Targeted Industries" 
                  fullWidth 
                  name="targeted_industries" 
                  value={formData.targeted_industries} 
                  onChange={handleInputTextChange}
                  onBlur={() => handleErrors('targeted_industries')}
                  error={formData.targeted_industries === '' && formErrors.targeted_industries}
                  helperText={(formData.targeted_industries === '' && formErrors.targeted_industries) ? 'Field is required' : ''}  
                />
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md flex " >
                <TextField  
                  variant="outlined"
                  label="Primary Motivations" 
                  fullWidth 
                  name="primary_motivations" 
                  value={formData.primary_motivations} 
                  onChange={handleInputTextChange}
                  onBlur={() => handleErrors('primary_motivations')}
                  error={formData.primary_motivations === '' && formErrors.primary_motivations}
                  helperText={(formData.primary_motivations === '' && formErrors.primary_motivations) ? 'Field is required' : ''}  
                />
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md flex " >
                <TextField  
                  variant="outlined"
                  label="Mitigation" 
                  fullWidth 
                  name="mitigation" 
                  value={formData.mitigation} 
                  onChange={handleInputTextChange}
                  onBlur={() => handleErrors('mitigation')}
                  error={formData.mitigation === '' && formErrors.mitigation}
                  helperText={(formData.mitigation === '' && formErrors.mitigation) ? 'Field is required' : ''}  
                />
              </div>
              
              <div className="card mb-2">
                  <div className="card-body">
                    <h5 class="card-title">Identified IOC's of ThreatActor</h5>
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md">
                      <Button variant="contained" onClick={handleAddField}>
                        Add IOC's Fields  
                      </Button>
                      <div className="note-content">
                        <p>
                          <strong>Note:</strong> You can use fields like <em>IP Address</em> as <code>ipaddress</code>, 
                          <em>Hash Values</em> as <code>hash_values</code>, and <em>Port Numbers</em> as 
                          <code> port_numbers</code> etc. 
                          Use <code> Comma(,) </code> to seperate the elements like <code> 22,25 port numbers</code>.
                        </p>
                      </div>
                    </div>
                    {
                      dynamicIOCFields.map((field, index) => (
                        <div className="row" key={index}>
                          <div className="col-lg-5 col-md-5 col-sm-12 col-xs-12 pa-md ">
                            <TextField
                              fullWidth
                              label="Field Name"
                              name="fieldName"
                              value={field.fieldName}
                              onChange={(e) => handleDynamicFieldChange(index, 'fieldName', e.target.value)}
                            />
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                            <TextField
                              fullWidth
                              label="Field Value"
                              name="fieldValue"
                              value={field.fieldValue}
                              onChange={(e) => handleDynamicFieldChange(index, 'fieldValue', e.target.value)}
                            />
                          </div>
                          <div className="col-lg-1 col-md-1 col-sm-6 col-xs-6 pa-md">
                            <IconButton onClick={() => handleRemoveField(index)}>
                              <DeleteIcon sx={{ color: 'red' }} />
                            </IconButton>
                          </div>
                        </div>
                  ))}
                    
                  </div>
              </div>

              <div className="card mb-2">
                  <div className="card-body">
                    <h5 class="card-title">Common TTP's</h5>
                    <div className="row col-lg-12">
                      <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md flex " >
                        <TextField  
                          variant="outlined"
                          label="Techniques" 
                          fullWidth 
                          name="techniques" 
                          value={formData.common_ttps.techniques} 
                          onChange={handleTTPsTextChange}
                          onBlur={() => handleErrors('techniques')}
                          error={formData.common_ttps.techniques === '' && formErrors.techniques}
                          helperText={(formData.common_ttps.techniques === '' && formErrors.techniques) ? 'Field is required' : ''}  
                        />
                      </div>
                      <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md flex " >
                        <TextField  
                          variant="outlined"
                          label="Tactics" 
                          fullWidth 
                          name="tactics" 
                          value={formData.common_ttps.tactics} 
                          onChange={handleTTPsTextChange}
                          onBlur={() => handleErrors('tactics')}
                          error={formData.common_ttps.tactics === '' && formErrors.tactics}
                          helperText={(formData.common_ttps.tactics === '' && formErrors.tactics) ? 'Field is required' : ''}  
                        />
                      </div>
                      <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md flex " >
                        <TextField  
                          variant="outlined"
                          label="Reference Links" 
                          fullWidth 
                          name="links" 
                          value={formData.common_ttps.links} 
                          onChange={handleTTPsTextChange}
                          onBlur={() => handleErrors('links')}
                          error={formData.common_ttps.links === '' && formErrors.links}
                          helperText={(formData.common_ttps.links === '' && formErrors.links) ? 'Field is required' : ''}  
                        />
                      </div>                      
                    </div>
                    <h5 class="card-title">Recent Activity</h5>
                    <div className="row col-lg-12">
                      <div className="col-lg-4 col-md-4 col-sm-12 col-xs-12 pa-md flex " >
                        <TextField  
                          variant="outlined"
                          label="Date" 
                          fullWidth 
                          name="date" 
                          value={formData.common_ttps.recent_activity.date} 
                          onChange={handleRecentActivityTextChange}
                          onBlur={() => handleErrors('date')}
                          error={formData.common_ttps.recent_activity.date === '' && formErrors.date}
                          helperText={(formData.common_ttps.recent_activity.date === '' && formErrors.date) ? 'Field is required' : ''}  
                        />
                      </div>
                      <div className="col-lg-8 col-md-8 col-sm-12 col-xs-12 pa-md flex " >
                        <TextField  
                          variant="outlined"
                          label="Targets" 
                          fullWidth 
                          name="targets" 
                          value={formData.common_ttps.recent_activity.targets} 
                          onChange={handleRecentActivityTextChange}
                          onBlur={() => handleErrors('targets')}
                          error={formData.common_ttps.recent_activity.targets === '' && formErrors.targets}
                          helperText={(formData.common_ttps.recent_activity.targets === '' && formErrors.targets) ? 'Field is required' : ''}  
                        />
                      </div>
                      <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md flex " >
                        <TextField  
                          variant="outlined"
                          label="News Link" 
                          fullWidth 
                          name="newslink" 
                          value={formData.common_ttps.recent_activity.newslink} 
                          onChange={handleRecentActivityTextChange}
                          onBlur={() => handleErrors('aliases')}
                          error={formData.aliases === '' && formErrors.aliases}
                          helperText={(formData.aliases === '' && formErrors.aliases) ? 'Field is required' : ''}  
                        />
                      </div>
                      <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md flex " >
                        <TextField  
                          variant="outlined"
                          label="Description" 
                          fullWidth 
                          name="description" 
                          value={formData.common_ttps.recent_activity.description} 
                          onChange={handleRecentActivityTextChange}
                          onBlur={() => handleErrors('description')}
                          error={formData.common_ttps.recent_activity.description === '' && formErrors.description}
                          helperText={(formData.common_ttps.recent_activity.description === '' && formErrors.description) ? 'Field is required' : ''}  
                        />
                      </div>                      
                    </div>
                  </div>
              </div>

              <div className="card">
                  <div className="card-body">
                    <h5 class="card-title">Historical Attacks</h5>
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md">
                      <Button variant="contained" onClick={handleAddAttack}>
                        Add Attack
                      </Button>
                    </div>
                    {dynamicAttacks.map((attack, index) => (
                        <div key={index} className="row">
                          <div className="col-lg-10 col-md-10 col-sm-10 col-xs-10 " ></div>
                          <div className="col-lg-2 col-md-2 col-sm-2 col-xs-2 d-flex justify-content-end" >
                            <IconButton onClick={() => handleRemoveAttack(index)}>
                              <DeleteIcon sx={{ color: "red" }} />
                            </IconButton>
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md " >
                            <FormControl fullWidth>
                              <TextField
                                label="Date"
                                value={attack.date}
                                onChange={(e) => handleAttackFieldChange(index, "date", e.target.value)}
                                fullWidth
                              />
                            </FormControl>
                          </div>
                          <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md " >
                            <FormControl fullWidth>
                              <TextField
                                label="Targets"
                                value={attack.targets}
                                onChange={(e) => handleAttackFieldChange(index, "targets", e.target.value)}
                                fullWidth
                                className="col-lg-4 col-md-4 col-sm-12"
                              />
                            </FormControl>
                          </div>
                          <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md " >
                            <FormControl fullWidth>
                              <TextField
                                label="Description"
                                value={attack.description}
                                onChange={(e) => handleAttackFieldChange(index, "description", e.target.value)}
                                fullWidth
                                multiline
                                rows={2}
                                maxRows={Infinity}
                              />
                            </FormControl>
                          </div>
                        </div>
                      ))}
                    
                  </div>
              </div>
              
            </div>
            <div className="center-card radio-card">
              <Button variant="contained" onClick={(e) => handleSubmit(e)}>Submit</Button>
            </div>
            <p className="error-message">{errormessage}</p>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
}
export default EditThreatActor; 
